import React from 'react';

import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';
import { useAutoNavigate } from '@betterment/js-runtime';
import { Box } from '@betterment/samba-react';
import * as Sentry from '@sentry/browser';
import { InlineWidget, useCalendlyEventListener } from 'react-calendly';

import { AdvisorNetworkIntentStatus } from '../../graphql/schema';
import { WorkflowLayout } from '../../Shared/layout/WorkflowLayout';
import { UpdateAdvisorNetworkIntentDocument } from '../graphql/mutation.updateAdvisorNetworkIntent.generated';
import { ScheduledEventConfigurationDocument } from '../graphql/query.scheduledEventConfiguration.generated';

import type { StepProps } from '@betterment/js-runtime';

export const ScheduleMeetingStep: React.FC<StepProps> = ({ flowState, goToNextStep, goToPreviousStep }) => {
  const navigate = useAutoNavigate();

  const { data } = useGraphQLQuery(ScheduledEventConfigurationDocument, {
    throwOnError: true,
  });
  const [setAdvisorNetworkIntentToInterested] = useGraphQLMutation(UpdateAdvisorNetworkIntentDocument, {
    variables: {
      input: {
        status: AdvisorNetworkIntentStatus.Interested,
      },
    },
  });

  useCalendlyEventListener({
    onEventScheduled: async _ => {
      const result = await setAdvisorNetworkIntentToInterested();

      if (result.errors || !result.data) {
        // At this point the calendar event has already been created successfully.
        // We want this to fail gracefully, so it doesn't block the user from closing the flow.
        Sentry.captureMessage('Failed to update advisor network intent status to Interested', {
          extra: { errors: result.errors },
        });
      }

      goToNextStep();
    },
  });

  if (data?.financialServiceOptions?.[0].__typename !== 'BettermentAdvisorNetworkOption') {
    return null;
  }

  const {
    scheduledEventConfiguration: { calendarLink },
  } = data.financialServiceOptions[0];
  const { firstName, lastName, email, phoneNumber } = data.user;

  return (
    <WorkflowLayout
      feature="advisor_network"
      pageTitle="Schedule Appointment"
      workflowTitle=""
      onBack={goToPreviousStep}
      onClose={() => navigate(flowState.returnToPath)}
      size="full"
    >
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'space.300',
          width: '100%',
          maxWidth: '1000px',
          margin: '0 auto',
        }}
        id="calendly-wrapper"
      >
        <InlineWidget
          url={calendarLink}
          prefill={{
            firstName,
            lastName,
            email,
            customAnswers: {
              a1: `1${phoneNumber}`,
            },
          }}
          styles={{
            width: '100%',
            height: '700px',
          }}
        />
      </Box>
    </WorkflowLayout>
  );
};
